/** Разделитель имени таблицы и идентификатора пресета в ключе `tables`: `<tableName>:<uuid>` */
const PRESET_KEY_SEPARATOR = ':';

/**
 * Разделитель имени таблицы и пресета в ключе рабочей записи его раздела: `<tableName>@<uuid>`.
 * Отличается от разделителя пресета, чтобы запись раздела не считалась пресетом таблицы.
 */
const SECTION_KEY_SEPARATOR = '@';

/** Собирает полный идентификатор пресета по имени таблицы и uuid. */
export const buildPresetId = (tableName: string, uuid: string): string => `${tableName}${PRESET_KEY_SEPARATOR}${uuid}`;

/** Проверяет, что ключ в `tables` относится к указанной таблице как пресет. */
export const isPresetKeyOfTable = (key: string, tableName: string): boolean =>
  key.startsWith(`${tableName}${PRESET_KEY_SEPARATOR}`);

/** Метка системного пресета в его ключе: `<tableName>:system:<id записи сервера>` */
const SYSTEM_PRESET_MARKER = 'system';

/** Метка совместного пресета в его ключе: `<tableName>:shared:<id записи сервера>` */
const SHARED_PRESET_MARKER = 'shared';

const serverPresetKeyPrefix = (tableName: string, marker: string): string =>
  `${tableName}${PRESET_KEY_SEPARATOR}${marker}${PRESET_KEY_SEPARATOR}`;

const parseServerPresetId = (presetId: string, tableName: string, marker: string): number | null => {
  const prefix = serverPresetKeyPrefix(tableName, marker);
  if (!presetId.startsWith(prefix)) return null;
  const serverId = Number(presetId.slice(prefix.length));
  return Number.isInteger(serverId) ? serverId : null;
};

/** Собирает ключ системного пресета таблицы по идентификатору его записи на сервере. */
export const buildSystemPresetId = (tableName: string, serverId: number): string =>
  `${serverPresetKeyPrefix(tableName, SYSTEM_PRESET_MARKER)}${serverId}`;

/**
 * Возвращает идентификатор серверной записи системного пресета либо `null`, если ключ
 * принадлежит пресету другого вида. По нему экшены и различают вид пресета.
 */
export const parseSystemPresetId = (presetId: string, tableName: string): number | null =>
  parseServerPresetId(presetId, tableName, SYSTEM_PRESET_MARKER);

/** Собирает ключ совместного пресета таблицы по идентификатору его записи на сервере. */
export const buildSharedPresetId = (tableName: string, serverId: number): string =>
  `${serverPresetKeyPrefix(tableName, SHARED_PRESET_MARKER)}${serverId}`;

/**
 * Возвращает идентификатор серверной записи совместного пресета либо `null`, если ключ
 * принадлежит пресету другого вида.
 */
export const parseSharedPresetId = (presetId: string, tableName: string): number | null =>
  parseServerPresetId(presetId, tableName, SHARED_PRESET_MARKER);

/**
 * Ключ рабочей записи раздела закреплённого пресета в `tables`.
 *
 * Раздел ведёт свой вид списка наравне с основным разделом таблицы (её рабочей записью под именем
 * таблицы): каждый раздел помнит, каким пользователь его оставил, поэтому вид у них раздельный.
 */
export const buildPresetSectionKey = (presetId: string): string =>
  presetId.replace(PRESET_KEY_SEPARATOR, SECTION_KEY_SEPARATOR);

/**
 * Ключ пресета, раздел которого ведёт рабочая запись; `null` — запись не раздел пресета, а основной
 * раздел таблицы. Обратна {@link buildPresetSectionKey}.
 */
export const parsePresetSectionKey = (sectionKey: string): string | null =>
  sectionKey.includes(SECTION_KEY_SEPARATOR) ? sectionKey.replace(SECTION_KEY_SEPARATOR, PRESET_KEY_SEPARATOR) : null;

/**
 * Имя таблицы, к которой относится ключ рабочей записи: сам ключ у основного раздела,
 * часть до разделителя — у раздела закреплённого пресета.
 */
export const getSectionTableName = (sectionKey: string): string => sectionKey.split(SECTION_KEY_SEPARATOR)[0];
